import React, { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { BusyButton } from "../../components/etams/BusyButton";
import { toast } from "sonner";
import { createAset, updateAset, apiErrorMessage } from "../../lib/asetApi";
import { label } from "../../hooks/useAsetMeta";
import { cariKaryawanRegion, ambilKaryawan, useRegions, useStores } from "./bantuAset";
import PemilihCari from "../../components/etams/PemilihCari";
import { labelKaryawan } from "../../lib/sumberPilihan";

// Form buat/ubah aset (4b-1). Cermin `aset_service.buat_aset` / `ubah_aset`:
//   * OWNER wajib memilih region; KC region profil (terkunci — pola Holidays).
//   * kategori TERKUNCI sesudah dibuat (FK komposit jadwal/km) — di mode ubah
//     nol ditampilkan sebagai isian.
//   * status nol ada di sini: awal selalu AKTIF, ubah lewat POST /status.
//   * PIC WAJIB (vonis Owner 15 Sep) — dropdown karyawan aktif region aset.
//   * Galat backend (400/403/409) ditampilkan verbatim.

const KOSONG = {
  nama: "", kategori: "", region_id: "", jenis: "", nomor_seri: "", tanggal_beli: "",
  harga_beli_idr: "", vendor: "", store_id: "", pic_employee_id: "", catatan: "",
};
const TANPA = "__tanpa__";

export default function AsetFormDialog({ terbuka, onTutup, onTersimpan, meta, aset, isOwner, regionProfil, regionNama }) {
  const ubah = !!aset;
  const [form, setForm] = useState(KOSONG);
  const [menyimpan, setMenyimpan] = useState(false);
  const regions = useRegions(isOwner);
  const stores = useStores();
  const regionTerpilih = ubah ? aset.region_id : (isOwner ? form.region_id : regionProfil);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!terbuka) return;
    setForm(aset ? {
      nama: aset.nama || "", kategori: aset.kategori || "", region_id: aset.region_id || "",
      jenis: aset.jenis || "", nomor_seri: aset.nomor_seri || "", tanggal_beli: aset.tanggal_beli || "",
      harga_beli_idr: aset.harga_beli_idr ?? "", vendor: aset.vendor || "", store_id: aset.store_id || "",
      pic_employee_id: aset.pic_employee_id || "", catatan: aset.catatan || "",
    } : { ...KOSONG, region_id: isOwner ? "" : (regionProfil || "") });
  }, [terbuka, aset, isOwner, regionProfil]);

  const storesRegion = stores.filter((s) => !regionTerpilih || s.region_id === regionTerpilih);

  const simpan = async () => {
    if (!form.nama.trim()) { toast.error("Nama aset wajib diisi."); return; }
    if (!ubah && !form.kategori) { toast.error("Kategori wajib dipilih."); return; }
    if (!ubah && isOwner && !form.region_id) { toast.error("Wilayah wajib dipilih."); return; }
    if (!form.pic_employee_id) { toast.error("PIC aset wajib dipilih."); return; }
    setMenyimpan(true);
    try {
      const opsional = (v) => (v === "" || v === null || v === undefined ? null : v);
      const isi = {
        nama: form.nama.trim(),
        jenis: opsional(form.jenis.trim()),
        nomor_seri: opsional(form.nomor_seri.trim()),
        tanggal_beli: opsional(form.tanggal_beli),
        harga_beli_idr: form.harga_beli_idr === "" ? null : Number(form.harga_beli_idr),
        vendor: opsional(form.vendor.trim()),
        store_id: opsional(form.store_id),
        pic_employee_id: form.pic_employee_id,
        catatan: opsional(form.catatan.trim()),
      };
      if (ubah) {
        // PATCH hanya KOLOM_ASET_UBAH; kirim yang berubah saja supaya nol
        // menyentuh kolom yang nol disentuh orangnya.
        const body = {};
        Object.entries(isi).forEach(([k, v]) => {
          const lama = aset[k] === undefined ? null : aset[k];
          if ((v ?? null) !== (lama ?? null)) body[k] = v;
        });
        if (Object.keys(body).length === 0) { toast.message("Tidak ada yang berubah."); onTutup(); return; }
        const r = await updateAset(aset.id, body);
        toast.success(`Aset ${r.data?.kode || ""} diperbarui.`);
        onTersimpan(r.data);
      } else {
        const body = { ...isi, kategori: form.kategori };
        Object.keys(body).forEach((k) => { if (body[k] === null) delete body[k]; });
        if (isOwner) body.region_id = form.region_id;
        const r = await createAset(body);
        toast.success(`Aset ${r.data?.kode || ""} dibuat.`);
        onTersimpan(r.data);
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal menyimpan aset."));
    } finally {
      setMenyimpan(false);
    }
  };

  return (
    <Dialog open={terbuka} onOpenChange={(o) => { if (!o) onTutup(); }}>
      <DialogContent className="max-w-2xl" data-testid="aset-form-dialog">
        <DialogHeader>
          <DialogTitle>{ubah ? `Ubah Aset ${aset.kode}` : "Aset Baru"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Nama Aset *</Label>
              <Input value={form.nama} onChange={(e) => upd("nama", e.target.value)} data-testid="aset-form-nama" />
            </div>
            <div>
              <Label>Kategori *</Label>
              {ubah ? (
                <Input value={label(meta, "kategori", aset.kategori)} disabled data-testid="aset-form-kategori-terkunci" />
              ) : (
                <Select value={form.kategori} onValueChange={(v) => upd("kategori", v)}>
                  <SelectTrigger data-testid="aset-form-kategori"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
                  <SelectContent>
                    {(meta?.kategori || []).map((k) => <SelectItem key={k.code} value={k.code}>{k.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {ubah && <div className="text-xs text-muted-foreground mt-1">Kategori tidak bisa diubah setelah aset dibuat.</div>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Wilayah *</Label>
              {isOwner && !ubah ? (
                <Select value={form.region_id} onValueChange={(v) => { upd("region_id", v); upd("store_id", ""); upd("pic_employee_id", ""); }}>
                  <SelectTrigger data-testid="aset-form-region"><SelectValue placeholder="Pilih wilayah" /></SelectTrigger>
                  <SelectContent>{regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input value={regions.find((r) => r.id === regionTerpilih)?.name || regionNama || "—"}
                       disabled data-testid="aset-form-region-terkunci" />
              )}
            </div>
            <div>
              <Label>Cabang</Label>
              <Select value={form.store_id || TANPA} onValueChange={(v) => upd("store_id", v === TANPA ? "" : v)}>
                <SelectTrigger data-testid="aset-form-store"><SelectValue placeholder="Tanpa cabang" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={TANPA}>Tanpa cabang</SelectItem>
                  {storesRegion.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>PIC Aset *</Label>
            {/* Pola daftar seragam (28 Sep 2026): karyawan DICARI di server, bukan seluruh region di satu dropdown. */}
            <PemilihCari key={regionTerpilih || "tanpa-region"} value={form.pic_employee_id || null}
                         onChange={(k) => upd("pic_employee_id", k?.id || "")} ambilSatu={ambilKaryawan}
                         cari={cariKaryawanRegion(regionTerpilih)} label={labelKaryawan} tampilAwal
                         disabled={!regionTerpilih} placeholder={!regionTerpilih ? "Pilih wilayah dulu" : "Pilih karyawan"}
                         placeholderCari="Ketik nama atau kode karyawan…" testId="aset-form-pic" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Jenis / Merek / Tipe</Label>
              <Input value={form.jenis} onChange={(e) => upd("jenis", e.target.value)} data-testid="aset-form-jenis" />
            </div>
            <div>
              <Label>Nomor Seri</Label>
              <Input value={form.nomor_seri} onChange={(e) => upd("nomor_seri", e.target.value)} data-testid="aset-form-nomor-seri" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label>Tanggal Beli</Label>
              <Input type="date" value={form.tanggal_beli} onChange={(e) => upd("tanggal_beli", e.target.value)} data-testid="aset-form-tanggal-beli" />
            </div>
            <div>
              <Label>Harga Beli (Rp)</Label>
              <Input type="number" min="0" value={form.harga_beli_idr} onChange={(e) => upd("harga_beli_idr", e.target.value)} data-testid="aset-form-harga" />
            </div>
            <div>
              <Label>Vendor / Penjual</Label>
              <Input value={form.vendor} onChange={(e) => upd("vendor", e.target.value)} data-testid="aset-form-vendor" />
            </div>
          </div>

          <div>
            <Label>Catatan</Label>
            <Textarea rows={2} value={form.catatan} onChange={(e) => upd("catatan", e.target.value)} data-testid="aset-form-catatan" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onTutup} disabled={menyimpan}>Batal</Button>
          <BusyButton onClick={simpan} busy={menyimpan} busyText="Menyimpan..." data-testid="aset-form-simpan">Simpan</BusyButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
